import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Users, Search, Activity, FolderKanban, Database, MessageSquare, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try { const res = await adminAPI.users(); setUsers(res.data.users || []); } catch {}
    setLoading(false);
  };

  const loadUserActivity = async (userId) => {
    setLoadingActivity(true);
    try { const res = await adminAPI.getUserActivity(userId); setUserActivity(res.data); } catch {}
    setLoadingActivity(false);
  };

  const openUser = (user) => { setSelected(user); setNewPassword(''); loadUserActivity(user.id); };

  const updateRole = async (userId, role) => {
    try { await adminAPI.updateUser(userId, { role }); toast.success('Role updated'); loadUsers(); } catch { toast.error('Failed'); }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Min 6 characters'); return; }
    setResetting(true);
    try { await adminAPI.resetPassword(selected.id, { new_password: newPassword }); toast.success('Password reset'); setNewPassword(''); } catch { toast.error('Failed'); }
    setResetting(false);
  };

  const filtered = users.filter(u => !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-4 sm:p-6 max-w-[1400px]" data-testid="admin-users">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Users</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">{users.length} registered users</p>
        </div>
        <div className="relative w-full sm:w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9 text-xs h-8" />
        </div>
      </div>

      {/* Desktop: Table | Mobile: Cards */}
      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
      ) : (
        <>
          {/* Desktop Table */}
          <Card className="hidden sm:block">
            <ScrollArea className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Email</TableHead>
                    <TableHead className="text-xs">Role</TableHead>
                    <TableHead className="text-xs">Plan</TableHead>
                    <TableHead className="text-xs">Joined</TableHead>
                    <TableHead className="text-xs w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(user => (
                    <TableRow key={user.id} className="cursor-pointer hover:bg-muted/30" onClick={() => openUser(user)}>
                      <TableCell className="text-xs font-medium">{user.name || '\u2014'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{user.email}</TableCell>
                      <TableCell>
                        <Select value={user.role} onValueChange={(v) => updateRole(user.id, v)}>
                          <SelectTrigger className="h-6 text-[10px] w-24" onClick={(e) => e.stopPropagation()}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="analyst">Analyst</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="superadmin">SuperAdmin</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs">{user.plan || 'free'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '\u2014'}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={(e) => { e.stopPropagation(); openUser(user); }}>View</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </Card>

          {/* Mobile Cards */}
          <div className="sm:hidden space-y-2">
            {filtered.map(user => (
              <Card key={user.id} className="cursor-pointer active:scale-[0.99] transition-transform" onClick={() => openUser(user)}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{user.name || 'No Name'}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Badge variant="outline" className="text-[9px]">{user.role}</Badge>
                      <Badge variant="outline" className="text-[9px]">{user.plan || 'free'}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* User Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setUserActivity(null); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>{selected?.name}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-[10px] sm:text-xs">
                <div><span className="text-muted-foreground">Email:</span> {selected.email}</div>
                <div><span className="text-muted-foreground">Role:</span> {selected.role}</div>
                <div><span className="text-muted-foreground">Plan:</span> {selected.plan || 'free'}</div>
                <div><span className="text-muted-foreground">Joined:</span> {selected.created_at ? new Date(selected.created_at).toLocaleDateString() : '\u2014'}</div>
              </div>

              {userActivity && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { icon: FolderKanban, color: 'text-emerald-400', val: userActivity.stats.total_projects, label: 'Projects' },
                    { icon: Database, color: 'text-purple-400', val: userActivity.stats.total_datasets, label: 'Datasets' },
                    { icon: MessageSquare, color: 'text-cyan-400', val: userActivity.stats.total_messages, label: 'Messages' },
                    { icon: Activity, color: 'text-amber-400', val: userActivity.stats.total_sessions, label: 'Sessions' },
                  ].map((s, i) => (
                    <Card key={i}><CardContent className="p-2.5 text-center"><s.icon className={`w-3.5 h-3.5 mx-auto ${s.color}`} /><p className="text-base sm:text-lg font-bold mt-0.5">{s.val}</p><p className="text-[9px] text-muted-foreground">{s.label}</p></CardContent></Card>
                  ))}
                </div>
              )}

              {loadingActivity ? (
                <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
              ) : userActivity?.activity?.length > 0 ? (
                <div>
                  <p className="text-xs font-medium mb-1.5">Recent Activity</p>
                  <div className="space-y-0.5 max-h-[180px] overflow-y-auto">
                    {userActivity.activity.map((evt, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[10px] sm:text-xs py-1 border-b border-border/30">
                        <Badge variant="outline" className="text-[8px] sm:text-[9px] h-4">{evt.event_name?.replace(/_/g, ' ')}</Badge>
                        <span className="flex-1 text-muted-foreground truncate">{evt.metadata?.message_preview || evt.metadata?.name || ''}</span>
                        <span className="text-[9px] text-muted-foreground flex-shrink-0">{new Date(evt.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="border-t border-border pt-3">
                <p className="text-xs font-medium mb-1.5 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Reset Password</p>
                <div className="flex gap-2">
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 6)" className="text-xs h-8 flex-1" />
                  <Button size="sm" className="h-8 text-xs flex-shrink-0" onClick={resetPassword} disabled={resetting}>
                    {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reset'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
